import React, { useEffect, useState } from "react";
import { useFieldArray, useWatch } from "react-hook-form";
import {ReactHookFormSelect} from "../../nuup/componentes/Forms/ReactHookFormSelect.jsx";
import {ReactHookFormInput} from "../../nuup/componentes/Forms/ReactHookFormInput.jsx";
import Api from "../../api/global.service";
import { CALIDAD_AUTO_DEFECTOS_CONTEO } from "./config/feature-flags.js";

const CalidadAnalisisFisicoDefectos = ({
  type,
  control,
  register,
  setValue,
  getValues,
  errors,
}) => {
  const { fields, append, remove, insert } = useFieldArray({
    control,
    name: type,
  });

  const [mapDefectos, setMapDefectos] = useState({});
  const watchedRows = useWatch({ control, name: type });

  // Cargar definiciones desde API (dinámico)
  useEffect(() => {
    if (!CALIDAD_AUTO_DEFECTOS_CONTEO) return;
    let ignore = false;
    Api.getAll('calidad/config/defectos')
      .then(res => {
        const rows = res?.data?.data || [];
        const map = {};
        rows.forEach(r => {
          const key = String(r.id_defecto);
          const ratio = parseFloat(r.granos_por_defecto);
          if (!isNaN(ratio) && ratio > 0) {
            map[key] = ratio;
          }
        });
        if (!ignore) setMapDefectos(map);
      })
      .catch(() => {});
    return () => { ignore = true; };
  }, []);

  // Autocompletar campo "defectos" en decimales (resultado intermedio) a partir de granos
  useEffect(() => {
    if (!CALIDAD_AUTO_DEFECTOS_CONTEO) return;
    if (!watchedRows || Object.keys(mapDefectos).length === 0) return;
    watchedRows.forEach((row, idx) => {
      const idTipo = row?.id_tipo_mancha;
      const granos = row?.no_granos ?? row?.granos;
      const autoFlag = (row?.defectos_auto ?? 1) !== 0; // por defecto, auto
      const granosEmpty = granos === undefined || granos === null || String(granos).trim() === '';

      // Si se limpia 'granos', limpiar el valor propuesto de 'defectos' cuando está en auto
      if (autoFlag && granosEmpty) {
        const currentRaw = row?.defectos;
        const isAlreadyEmpty = String(currentRaw ?? '') === '';
        if (!isAlreadyEmpty) {
          setValue(`${type}.${idx}.defectos`, '');
        }
        return;
      }

      if (!idTipo) {
        if (autoFlag) {
          const currentRaw = row?.defectos;
          const isAlreadyEmpty = String(currentRaw ?? '') === '';
          if (!isAlreadyEmpty) {
            setValue(`${type}.${idx}.defectos`, '');
          }
        }
        return;
      }

      const ratio = mapDefectos[String(idTipo)];
      if (!ratio) return;

      // Resultado intermedio: defectos fraccionarios con dos decimales
      const raw = Number(granos) / Number(ratio);
      const proposed = (isFinite(raw) && !isNaN(raw)) ? Number(raw.toFixed(2)) : '';
      if (autoFlag) {
        const currentRaw = row?.defectos;
        const isSame = String(currentRaw ?? '') === String(proposed);
        if (!isSame) {
          setValue(`${type}.${idx}.defectos`, proposed);
        }
      }
    });
  }, [watchedRows, mapDefectos]);

  const eliminaDefecto = (index) => {
    let id_calidad_analisis_fisicos_defecto = getValues()?.[type]?.[index]?.id_calidad_analisis_fisicos_defecto;
    if(id_calidad_analisis_fisicos_defecto > 0){
      setValue(`${type}[${index}].eliminar`, 1);
    }else{
      remove(index);
    }
  }

  return (
    <>
      {fields.map((item, k) => {
        return (
          <div key={item.id} style={{display : getValues()?.[type]?.[k]?.eliminar === 1 ? 'none' : 'block'}}>
          <div className="row mt-1">
            <div className="col-lg-12 mt-2">

              {k === 0 && (
              <span style={{fontSize : 14,fontWeight : 'bold'}}>
                {"Defectos " +
                  (type === "analisis_oro.defectos_primarios" ? "primarios" : "secundarios")}
              </span>
              )}
            </div>

            <div className="col-xs-12 col-xl-3 mt-2">
              <ReactHookFormSelect
                placeholder="Tipo de mancha / defecto"
                label="Tipo de mancha / defecto"
                options={[]}
                id={`${type}[${k}].id_tipo_mancha`}
                name={`${type}[${k}].id_tipo_mancha`}
                error={errors?.[type]?.[k]?.id_tipo_mancha ? true : false}
                url={
                  "calidad/catalogos/defectos_" +
                  (type === "analisis_oro.defectos_primarios" ? 'primarios' : 'secundarios')
                }
                keyArray="id"
                labelArray="nombre"
                value={getValues()?.[type]?.[k]?.id_tipo_mancha}
                apiSearch={false} 
                control={control}
              />
              {errors?.[type]?.[k]?.id_tipo_mancha && (
                <div className="invalid-feedback">
                  {errors?.[type]?.[k]?.id_tipo_mancha.message}
                </div>
              )}
            </div>

            <div className="col-xs-5 col-xl-3 mt-1">
              <ReactHookFormInput
                type="number"
                id={`${type}.${k}.no_granos`}
                name={`${type}.${k}.no_granos`}
                error={errors?.[type]?.[k]?.no_granos}
                label="Granos"
                value={getValues()?.[type]?.[k]?.no_granos}
                control={control}
                readOnly={!(Number(getValues(`${type}.${k}.id_tipo_mancha`)) > 0)}
              />
            </div>
            <div className="col-xs-5 col-xl-3 mt-1">
              <ReactHookFormInput
                type="number"
                id={`${type}.${k}.defectos`}
                name={`${type}.${k}.defectos`}
                error={errors?.[type]?.[k]?.defectos}
                label="Defectos"
                value={getValues()?.[type]?.[k]?.defectos}
                control={control}
                readOnly={!(Number(getValues(`${type}.${k}.id_tipo_mancha`)) > 0)}
                onChangeExternal={(evt, rhfOnChange) => {
                  // Cambios manuales en 'Defectos' desactivan el auto-cálculo para esta fila
                  rhfOnChange(evt);
                  setValue(`${type}.${k}.defectos_auto`, 0);
                }}
              />
            </div>
            {k === 0 && (
              <div className="col-xs-2 col-xl-1 col-xxl-1">

          <button
            type="button" 
            className="btn btn-outline-dark mt-1"
            onClick={() =>
              append({
                id_calidad_analisis_fisicos_defecto: 0,
                id_tipo_mancha: "",
                no_granos: "",
                defectos: "",
                defectos_auto: 1,
              })
            }
          >
            <i className="fa fa-plus"></i>
          </button>
          </div>
            )}

            {k > 0 && (
              <div className="col-xs-1 col-xl-1 col-xxl-1">
                <button
                  className="btn btn-outline-danger mx-1"
                  type="button"
                  onClick={() => eliminaDefecto(k)}
                >
                  <i style={{color: 'red'}} className="fa fa-trash"></i>
                </button>
              </div>
            )}
          </div>
          </div>
        );
      })}
      <div className="row">
        <div className="col-lg-6">

        </div>
      </div>
    </>
  );
};

export default CalidadAnalisisFisicoDefectos;
